import { Box, Button, CircularProgress, IconButton, Tooltip, useTheme } from '@mui/material';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import { CheckCircle, Eye, Pen } from 'lucide-react';

import { FormattedDate, FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import titleCase from 'utils/title-case';
import CloneScenarioDialog from 'views/bluviu-nutrition/features/scenarios/components/clone-scenario-dialog';
import DeleteScenarioDialog from 'views/bluviu-nutrition/features/scenarios/components/delete-scenario-dialog';
import { ScenarioListItem } from 'views/bluviu-nutrition/features/scenarios/types/scenario-list-item.type';
import { ScenariosParams } from 'views/bluviu-nutrition/features/scenarios/types/scenarios-params.type';
import { useScenarioStatusPolling } from 'views/bluviu-nutrition/hooks/use-scenario-status-polling';
import { useSelectedSite } from 'views/bluviu-nutrition/hooks/use-selected-site';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';

type Props = {
  scenario: ScenarioListItem;
};

const PAGE_NAME_ID = 'bluViuNutrition.listScenarios';

const ListScenarioRow: React.FC<Props> = ({ scenario }) => {
  const { selectedSiteId } = useSelectedSite();
  const history = useHistory();
  const params = useParams<ScenariosParams>();
  const theme = useTheme();

  const { isComplete, isRunning } = useScenarioStatusPolling(scenario);

  const handleOnClick = (id: string) => {
    return history.push(`/bluviu-nutrition/scenarios/${selectedSiteId}/${id}`);
  };

  const hasResults = scenario.state === ScenarioState.SCENARIO_RESULT_SENT;

  return (
    <ListRow onClick={() => handleOnClick(scenario.id)}>
      <ListCell>
        <Box display={'flex'} flexDirection={'row'} gap={4}>
          <Box display={'flex'} alignItems={'center'}>
            {isComplete ? (
              <CheckCircle size={18} style={{ alignSelf: 'center' }} />
            ) : isRunning ? (
              <CircularProgress size={18} style={{ alignSelf: 'center' }} />
            ) : null}
          </Box>
          {scenario.name}
        </Box>
      </ListCell>
      <ListCell>{titleCase(scenario.model)}</ListCell>
      <ListCell>
        <FormattedDate value={scenario.updatedDate} day="2-digit" month="short" year="numeric" />
      </ListCell>
      <ListCell>{titleCase(scenario.state)}</ListCell>
      <ListCell>
        <Box display={'flex'} flexDirection={'row'} gap={2} justifyContent={'flex-end'} alignItems={'center'}>
          {hasResults && (
            <Box>
              <Button
                sx={{ p: `${theme.spacing(2, 6)}`, minHeight: 32 }}
                variant="outlined"
                onClick={() => history.push(`/bluviu-nutrition/scenarios/${params.globalSiteId}/${scenario.id}/result`)}>
                Results
              </Button>
            </Box>
          )}
          <Box display={'flex'} flexDirection={'row'} height={'fit-content'}>
            <CloneScenarioDialog scenario={scenario} />
            <DeleteScenarioDialog
              scenario={{
                id: scenario.id,
                name: scenario.name,
                state: scenario.state,
              }}
            />
            <Tooltip
              title={<FormattedMessage id={hasResults ? `${PAGE_NAME_ID}.view` : `${PAGE_NAME_ID}.edit`} values={{ type: '' }} />}>
              <IconButton onClick={() => handleOnClick(scenario.id)}>{hasResults ? <Eye size={22} /> : <Pen size={22} />}</IconButton>
            </Tooltip>
          </Box>
        </Box>
      </ListCell>
    </ListRow>
  );
};

export default ListScenarioRow;
